import { useState, useCallback } from 'react';
import { supabase, Transaction } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface EnhancedCategorySuggestion {
  category_id: string;
  category_name: string;
  confidence_score: number;
  method: 'MERCHANT' | 'SIMILARITY' | 'KEYWORD' | 'HEURISTIC';
  explanation: string;
  score_details?: any;
}

export interface CategorizationStats {
  total_patterns: number;
  merchant_patterns: number;
  keyword_patterns: number;
  avg_confidence: number;
  locked_transactions: number;
  pending_suggestions: number;
}

export interface SuggestionSummary {
  method: string;
  count_total: number;
  avg_confidence: number;
  acceptance_rate: number;
}

export function useSmartCategorizationV2() {
  const { effectiveUserId } = useAuth();
  const [suggestions, setSuggestions] = useState<Map<string, EnhancedCategorySuggestion>>(new Map());
  const [isLearning, setIsLearning] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * Prépare les features pour toutes les transactions de l'utilisateur
   */
  const prepareFeatures = useCallback(async () => {
    if (!effectiveUserId) return 0;

    const { data, error } = await supabase.rpc('prepare_transaction_features', {
      p_user_id: effectiveUserId
    });

    if (error) {
      console.error('Error preparing features:', error);
      return 0;
    }

    return data as number;
  }, [effectiveUserId]);

  /**
   * Obtient une suggestion pour une transaction spécifique
   */
  const getSuggestionForTransaction = useCallback(async (
    transaction: Transaction
  ): Promise<EnhancedCategorySuggestion | null> => {
    if (!effectiveUserId || transaction.category_id) return null;

    // Vérifier s'il y a une suggestion existante
    const { data: existingSuggestion } = await supabase
      .from('categorization_suggestions')
      .select('*, categories(name)')
      .eq('transaction_id', transaction.id)
      .is('was_accepted', null)
      .maybeSingle();

    if (existingSuggestion) {
      return {
        category_id: existingSuggestion.suggested_category_id,
        category_name: (existingSuggestion as any).categories?.name || '',
        confidence_score: existingSuggestion.confidence_score,
        method: existingSuggestion.method || 'KEYWORD',
        explanation: existingSuggestion.explanation || 'Suggestion basée sur l\'historique',
        score_details: existingSuggestion.score_details
      };
    }

    // Générer une nouvelle suggestion
    const { data, error } = await supabase.rpc('suggest_category_for_transaction_v2', {
      p_transaction_id: transaction.id,
      p_user_id: effectiveUserId
    });

    if (error || !data || data.length === 0) {
      return null;
    }

    const suggestion = data[0];

    if (suggestion.confidence_score >= 0.70) {
      // Sauvegarder la suggestion
      await supabase
        .from('categorization_suggestions')
        .insert({
          user_id: effectiveUserId,
          transaction_id: transaction.id,
          suggested_category_id: suggestion.suggested_category_id,
          confidence_score: suggestion.confidence_score,
          method: suggestion.method,
          explanation: suggestion.explanation,
          score_details: suggestion.score_details,
          was_accepted: null
        });

      return {
        category_id: suggestion.suggested_category_id,
        category_name: suggestion.category_name,
        confidence_score: suggestion.confidence_score,
        method: suggestion.method,
        explanation: suggestion.explanation,
        score_details: suggestion.score_details
      };
    }

    return null;
  }, [effectiveUserId]);

  /**
   * Charge les suggestions pour un ensemble de transactions
   */
  const loadSuggestionsForTransactions = useCallback(async (
    transactions: Transaction[],
    limit: number = 50
  ) => {
    if (!effectiveUserId) return;

    setIsProcessing(true);
    try {
      const newSuggestions = new Map<string, EnhancedCategorySuggestion>();
      const uncategorizedTransactions = transactions
        .filter(t => !t.category_id || (t as any).categorization_status !== 'LOCKED')
        .slice(0, limit);

      if (uncategorizedTransactions.length === 0) {
        setSuggestions(newSuggestions);
        return;
      }

      // Charger les suggestions existantes
      const { data: existingSuggestions } = await supabase
        .from('categorization_suggestions')
        .select('*, categories(name)')
        .in('transaction_id', uncategorizedTransactions.map(t => t.id))
        .is('was_accepted', null);

      if (existingSuggestions) {
        existingSuggestions.forEach(suggestion => {
          newSuggestions.set(suggestion.transaction_id, {
            category_id: suggestion.suggested_category_id,
            category_name: (suggestion as any).categories?.name || '',
            confidence_score: suggestion.confidence_score,
            method: suggestion.method || 'KEYWORD',
            explanation: suggestion.explanation || 'Suggestion basée sur l\'historique',
            score_details: suggestion.score_details
          });
        });
      }

      // Générer de nouvelles suggestions pour les transactions sans suggestion
      const transactionsNeedingSuggestions = uncategorizedTransactions.filter(
        t => !newSuggestions.has(t.id)
      );

      if (transactionsNeedingSuggestions.length > 0) {
        await supabase.rpc('generate_suggestions_batch_v2', {
          p_user_id: effectiveUserId,
          p_limit: transactionsNeedingSuggestions.length
        });

        // Recharger les suggestions nouvellement créées
        const { data: newBatchSuggestions } = await supabase
          .from('categorization_suggestions')
          .select('*, categories(name)')
          .in('transaction_id', transactionsNeedingSuggestions.map(t => t.id))
          .is('was_accepted', null);

        if (newBatchSuggestions) {
          newBatchSuggestions.forEach(suggestion => {
            newSuggestions.set(suggestion.transaction_id, {
              category_id: suggestion.suggested_category_id,
              category_name: (suggestion as any).categories?.name || '',
              confidence_score: suggestion.confidence_score,
              method: suggestion.method || 'KEYWORD',
              explanation: suggestion.explanation || 'Suggestion basée sur l\'historique',
              score_details: suggestion.score_details
            });
          });
        }
      }

      setSuggestions(newSuggestions);
    } finally {
      setIsProcessing(false);
    }
  }, [effectiveUserId]);

  /**
   * Accepte une suggestion et lance l'apprentissage
   */
  const acceptSuggestion = useCallback(async (
    transactionId: string,
    categoryId: string
  ) => {
    if (!effectiveUserId) return;

    setIsLearning(true);
    try {
      // Vérifier si la catégorie est un transfert interne
      const { data: category } = await supabase
        .from('categories')
        .select('is_internal_transfer')
        .eq('id', categoryId)
        .single();

      const isInternalTransfer = category?.is_internal_transfer || false;

      // Mettre à jour la transaction avec le nouveau statut
      await supabase
        .from('transactions')
        .update({
          category_id: categoryId,
          is_internal_transfer: isInternalTransfer
        })
        .eq('id', transactionId)
        .eq('user_id', effectiveUserId);

      // Lancer l'apprentissage avec la nouvelle fonction v2
      await supabase.rpc('learn_from_categorization_v2', {
        p_transaction_id: transactionId,
        p_user_id: effectiveUserId,
        p_category_id: categoryId
      });

      // Retirer la suggestion de l'état local
      setSuggestions(prev => {
        const newMap = new Map(prev);
        newMap.delete(transactionId);
        return newMap;
      });
    } finally {
      setIsLearning(false);
    }
  }, [effectiveUserId]);

  /**
   * Rejette une suggestion
   */
  const rejectSuggestion = useCallback(async (
    transactionId: string,
    rejectedCategoryId: string
  ) => {
    if (!effectiveUserId) return;

    await supabase.rpc('reject_suggestion', {
      p_transaction_id: transactionId,
      p_user_id: effectiveUserId,
      p_rejected_category_id: rejectedCategoryId
    });

    setSuggestions(prev => {
      const newMap = new Map(prev);
      newMap.delete(transactionId);
      return newMap;
    });
  }, [effectiveUserId]);

  /**
   * Applique une catégorie à toutes les transactions similaires (warm-up)
   */
  const applyCategoryToSimilar = useCallback(async (
    sourceTransactionId: string,
    categoryId: string
  ): Promise<{ count: number; transactionIds: string[] }> => {
    if (!effectiveUserId) return { count: 0, transactionIds: [] };

    setIsProcessing(true);
    try {
      const { data, error } = await supabase.rpc('apply_category_to_similar_transactions', {
        p_source_transaction_id: sourceTransactionId,
        p_user_id: effectiveUserId,
        p_category_id: categoryId
      });

      if (error) {
        console.error('Error applying category to similar transactions:', error);
        return { count: 0, transactionIds: [] };
      }

      const result = data[0];
      return {
        count: result.applied_count || 0,
        transactionIds: result.transaction_ids || []
      };
    } finally {
      setIsProcessing(false);
    }
  }, [effectiveUserId]);

  /**
   * Apprentissage manuel (catégorisation manuelle)
   */
  const learnFromManualCategorization = useCallback(async (
    transactionId: string,
    categoryId: string
  ) => {
    if (!effectiveUserId) return;

    setIsLearning(true);
    try {
      await supabase.rpc('learn_from_categorization_v2', {
        p_transaction_id: transactionId,
        p_user_id: effectiveUserId,
        p_category_id: categoryId
      });
    } finally {
      setIsLearning(false);
    }
  }, [effectiveUserId]);

  /**
   * Compte les transactions similaires pour le warm-up
   */
  const countSimilarTransactions = useCallback(async (
    merchantKey: string,
    excludeTransactionId?: string
  ): Promise<number> => {
    if (!effectiveUserId) return 0;

    const { data, error } = await supabase.rpc('count_similar_transactions', {
      p_user_id: effectiveUserId,
      p_merchant_key: merchantKey,
      p_exclude_transaction_id: excludeTransactionId || null
    });

    if (error) {
      console.error('Error counting similar transactions:', error);
      return 0;
    }

    return data as number;
  }, [effectiveUserId]);

  /**
   * Obtient les statistiques de catégorisation
   */
  const getStats = useCallback(async (): Promise<CategorizationStats | null> => {
    if (!effectiveUserId) return null;

    const { data, error } = await supabase.rpc('get_categorization_stats', {
      p_user_id: effectiveUserId
    });

    if (error) {
      console.error('Error getting stats:', error);
      return null;
    }

    return data[0] as CategorizationStats;
  }, [effectiveUserId]);

  /**
   * Obtient un résumé des suggestions par méthode
   */
  const getSuggestionsSummary = useCallback(async (): Promise<SuggestionSummary[]> => {
    if (!effectiveUserId) return [];

    const { data, error } = await supabase.rpc('get_suggestions_summary', {
      p_user_id: effectiveUserId
    });

    if (error) {
      console.error('Error getting suggestions summary:', error);
      return [];
    }

    return data as SuggestionSummary[];
  }, [effectiveUserId]);

  /**
   * Regénère toutes les suggestions (utile après amélioration du moteur)
   */
  const regenerateAllSuggestions = useCallback(async (): Promise<number> => {
    if (!effectiveUserId) return 0;

    setIsProcessing(true);
    try {
      const { data, error } = await supabase.rpc('regenerate_all_suggestions', {
        p_user_id: effectiveUserId
      });

      if (error) {
        console.error('Error regenerating suggestions:', error);
        return 0;
      }

      return data as number;
    } finally {
      setIsProcessing(false);
    }
  }, [effectiveUserId]);

  return {
    suggestions,
    isLearning,
    isProcessing,
    prepareFeatures,
    getSuggestionForTransaction,
    loadSuggestionsForTransactions,
    acceptSuggestion,
    rejectSuggestion,
    applyCategoryToSimilar,
    learnFromManualCategorization,
    countSimilarTransactions,
    getStats,
    getSuggestionsSummary,
    regenerateAllSuggestions
  };
}
